import type { LucideIcon } from 'lucide-react';
import { BarChart3, CheckCircle2, FileWarning, Globe2, Mail, MapPin, QrCode, Repeat2, Send, Share2, ShieldCheck, SlidersHorizontal, Sparkles, UsersRound, Workflow } from 'lucide-react';

export type Feature = {
  icon: LucideIcon;
  title: string;
  description: string;
};

export type PainPoint = {
  problem: string;
  solution: string;
};

export type WorkflowStep = {
  label: string;
  title: string;
  description: string;
};

export type CaseStudy = {
  slug: string;
  clientName: string;
  category: string;
  title: string;
  shortDescription: string;
  problem: string;
  outcome: string;
  flow: Array<{
    label: string;
    title: string;
    description: string;
  }>;
  proofPoints: string[];
};

export const proofMetrics = [
  { value: 'One', label: 'place to prepare every campaign' },
  { value: 'Every', label: 'location gets the right context' },
  { value: 'No more', label: 'guessing which asset is live' },
  { value: 'Clear', label: 'proof when results come in' },
];

export const painPoints: PainPoint[] = [
  {
    problem: 'A launch gets delayed because the team is still chasing the right list, link, QR code, and store rules.',
    solution: 'B4 puts the campaign pieces in one workspace so everyone can see what is ready and what still needs attention.',
  },
  {
    problem: 'A guest scans a code at one location and lands on an offer that was meant for another.',
    solution: 'Location routing and public campaign assets stay tied to the campaign instead of living in a separate QR tool.',
  },
  {
    problem: 'Marketing can launch, but operations still cannot answer what happened or who responded.',
    solution: 'Scans, signups, clicks, and member actions return to the campaign so the team can explain the outcome.',
  },
  {
    problem: 'Every promotion feels custom-built, so the next one starts with the same scramble.',
    solution: 'B4 creates a repeatable launch path that gives teams confidence without making every campaign feel like a rescue mission.',
  },
];

export const featureGroups: Feature[] = [
  {
    icon: Mail,
    title: 'Campaign builder',
    description: 'Bring setup, content, audience filters, schedule, review, and launch state into one place so readiness is visible.',
  },
  {
    icon: Globe2,
    title: 'Website builder',
    description: 'Cover the Squarespace-style website job with brand pages, campaign pages, forms, and public content that stay connected to member capture and reporting.',
  },
  {
    icon: QrCode,
    title: 'Reusable QR and redirects',
    description: 'Keep QR routing reusable and location-aware, with scan activity visible when the campaign is reviewed.',
  },
  {
    icon: UsersRound,
    title: 'Member intelligence',
    description: 'Understand who is reachable, what they belong to, and how they responded without stitching exports together.',
  },
  {
    icon: BarChart3,
    title: 'Reporting that traces action',
    description: 'Connect results to the promotion that caused them, so client conversations are based on evidence instead of anecdotes.',
  },
  {
    icon: ShieldCheck,
    title: 'Brand control',
    description: 'Protect brand standards, location rules, and approval state while still letting teams move faster.',
  },
];

export const channels: Feature[] = [
  {
    icon: Mail,
    title: 'Email marketing',
    description: 'Prepare the message, audience, send test, and launch state without losing track of what was approved.',
  },
  {
    icon: Share2,
    title: 'Social posts',
    description: 'Keep scheduled social content connected to the campaign instead of treating it as a separate reminder.',
  },
  {
    icon: QrCode,
    title: 'QR redirects',
    description: 'Avoid reprinting and rerouting confusion by managing reusable codes from the campaign workspace.',
  },
  {
    icon: Globe2,
    title: 'Websites and landing pages',
    description: 'Give guests a polished public destination for brand content, offers, signups, and store-specific context.',
  },
  {
    icon: Sparkles,
    title: 'Promo signup',
    description: 'Capture interest cleanly, even when brand relationship and marketing permission need to be handled separately.',
  },
  {
    icon: Send,
    title: 'Future SMS and push',
    description: 'Extend into direct messages without making the team relearn the launch process.',
  },
];

export const workflowSteps: WorkflowStep[] = [
  {
    label: '01',
    title: 'Start with the brand and locations',
    description: 'Choose who the campaign is for before content, pages, codes, or reports drift away from the real business context.',
  },
  {
    label: '02',
    title: 'Build the campaign pieces',
    description: 'Prepare email, landing pages, signup forms, QR redirects, and social posts without splitting them into disconnected projects.',
  },
  {
    label: '03',
    title: 'Review what will actually go live',
    description: 'See the audience, locations, public links, and channel status before the promotion reaches guests.',
  },
  {
    label: '04',
    title: 'Launch with fewer surprises',
    description: 'A shared readiness path lowers the chance of sending the wrong offer, publishing the wrong page, or missing a location.',
  },
  {
    label: '05',
    title: 'Explain the result',
    description: 'Bring scans, signups, clicks, member actions, and campaign performance back to the people who need to defend the spend.',
  },
];

export const operatorBenefits = [
  { icon: SlidersHorizontal, label: 'No channel-by-channel reconfiguration' },
  { icon: Repeat2, label: 'No duplicated audience imports' },
  { icon: MapPin, label: 'No lost location context' },
  { icon: FileWarning, label: 'No hidden campaign state' },
  { icon: Workflow, label: 'No last-minute launch scramble' },
  { icon: CheckCircle2, label: 'No public launch without readiness' },
];

export const caseStudies: CaseStudy[] = [
  {
    slug: 'table-tent-qr-promo',
    clientName: 'Marlow Street Grill',
    category: 'Restaurant table tent',
    title: 'A table tent QR code turns dine-in guests into reachable members.',
    shortDescription: 'A guest scans at the table, lands on a focused offer page, signs up, receives a promo, and becomes part of the next campaign audience.',
    problem: 'The restaurant had foot traffic, but every table visit ended as an anonymous guest unless staff manually pushed a loyalty signup.',
    outcome: 'The table became the acquisition moment. Guests joined from their own phones, received the right promo, and entered the member system with location context.',
    flow: [
      {
        label: '01',
        title: 'Table tent with QR code',
        description: 'A printed table tent sits at each location with a reusable QR code tied to the campaign and store.',
      },
      {
        label: '02',
        title: 'Guest lands on the offer page',
        description: 'The scan opens a branded landing page that explains the benefit without sending the guest to a generic website.',
      },
      {
        label: '03',
        title: 'Signup captures the relationship',
        description: 'The form captures email, phone, preferred location, and marketing permission in the same flow.',
      },
      {
        label: '04',
        title: 'Promo is delivered',
        description: 'The guest receives a welcome offer they can use on the next visit, making the signup feel immediately worthwhile.',
      },
      {
        label: '05',
        title: 'Member is ready for future campaigns',
        description: 'The new member is available for later email, QR, landing page, and location-based campaigns.',
      },
    ],
    proofPoints: [
      'Physical signage becomes measurable instead of decorative.',
      'Location context follows the guest into the member record.',
      'Promo delivery gives the guest a reason to return.',
      'Marketing can report scans, signups, and follow-up activity from one campaign.',
    ],
  },
  {
    slug: 'grand-opening-qr',
    clientName: 'Northstar Coffee House',
    category: 'Grand opening',
    title: 'A grand opening QR campaign builds the local list before the first rush is over.',
    shortDescription: 'A window poster and counter card send visitors to a launch page, collect early interest, and trigger a welcome promo.',
    problem: 'The team expected a busy opening week, but without a capture path, every curious visitor could disappear after the first purchase.',
    outcome: 'The opening campaign converted attention into owned contacts while the location was still top of mind.',
    flow: [
      {
        label: '01',
        title: 'Window and counter QR',
        description: 'Guests and passersby scan a QR code from a poster, counter card, or receipt insert.',
      },
      {
        label: '02',
        title: 'Launch landing page',
        description: 'The page introduces the new location, highlights the opening offer, and sets expectations for future updates.',
      },
      {
        label: '03',
        title: 'Signup joins the local audience',
        description: 'The form associates the guest with the new location so future campaigns can stay relevant.',
      },
      {
        label: '04',
        title: 'Welcome promo goes out',
        description: 'A first-week promo gives the guest a reason to return after the opening excitement fades.',
      },
      {
        label: '05',
        title: 'Campaign reports show early traction',
        description: 'The team can see which QR placements and landing page signups drove the new audience.',
      },
    ],
    proofPoints: [
      'Opening-week attention becomes a reusable audience.',
      'Local contacts are separated from broader brand traffic.',
      'The offer gives first-time visitors a second-visit reason.',
      'Launch performance can be reviewed without stitching together tools.',
    ],
  },
  {
    slug: 'event-booth-signup',
    clientName: 'Cedar Grove Market',
    category: 'Event booth',
    title: 'An event booth turns passing interest into a follow-up campaign.',
    shortDescription: 'A QR code at the booth leads to a product-specific signup page and sends a post-event promo to qualified visitors.',
    problem: 'The brand met interested shoppers at events, but paper lists and badge scans did not reliably turn into segmented follow-up.',
    outcome: 'Visitors joined from the booth, selected what they cared about, and entered a campaign audience the team could follow up with after the event.',
    flow: [
      {
        label: '01',
        title: 'Booth QR code',
        description: 'Staff point visitors to a QR code instead of asking them to write contact details on a clipboard.',
      },
      {
        label: '02',
        title: 'Event-specific landing page',
        description: 'The page speaks to the booth offer and asks for the preference data needed for useful follow-up.',
      },
      {
        label: '03',
        title: 'Signup creates the segment',
        description: 'Each submission captures source, interest, and permission so the follow-up list is already organized.',
      },
      {
        label: '04',
        title: 'Promo follows the event',
        description: 'A post-event offer is sent while the conversation is still fresh.',
      },
      {
        label: '05',
        title: 'Future campaigns start warmer',
        description: 'The brand can target event-acquired members later without guessing where they came from.',
      },
    ],
    proofPoints: [
      'The booth becomes a measurable acquisition channel.',
      'Interest tags are captured at signup instead of inferred later.',
      'Follow-up happens while the moment still feels relevant.',
      'Event contacts enter the same campaign system as other members.',
    ],
  },
  {
    slug: 'receipt-qr-winback',
    clientName: 'Palmetto Pizza Co.',
    category: 'Receipt QR',
    title: 'A receipt QR code gives recent buyers a simple reason to come back.',
    shortDescription: 'A QR code on the receipt routes guests to a bounce-back page, captures signup, and sends a next-order promo.',
    problem: 'Receipts were being printed anyway, but they were not creating any measurable return visit path.',
    outcome: 'The receipt became a low-friction invitation to join, claim a promo, and receive future campaigns.',
    flow: [
      {
        label: '01',
        title: 'Receipt QR code',
        description: 'A reusable QR code appears on receipts and bag inserts for takeout and delivery orders.',
      },
      {
        label: '02',
        title: 'Bounce-back landing page',
        description: 'The landing page frames the offer around the next order instead of a generic newsletter signup.',
      },
      {
        label: '03',
        title: 'Signup connects to purchase context',
        description: 'The member joins with source and location context, making later campaigns more useful.',
      },
      {
        label: '04',
        title: 'Next-order promo is sent',
        description: 'The promo gives the guest a practical reason to reorder soon.',
      },
      {
        label: '05',
        title: 'Repeat audience grows',
        description: 'Each scan feeds a future audience for winback, local offers, and seasonal campaigns.',
      },
    ],
    proofPoints: [
      'A passive receipt becomes a trackable acquisition path.',
      'The offer is tied to a recent purchase moment.',
      'Guests are captured with source and location context.',
      'The brand grows a repeat audience without adding staff work.',
    ],
  },
];
